import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { platformLabel } from '@/lib/platforms'

export interface PlatformFeatures {
  hashtags: boolean
  mentions: boolean
  first_comment: boolean
  threads: boolean
  article_link: boolean
}

export interface PlatformCapability {
  max_length: number
  supported_media: string[]
  features: PlatformFeatures
}

export interface PlatformOverrideData {
  body: string
  first_comment: string
  thread: boolean
  article_link: string
}

interface PlatformTabProps {
  platform: string
  capabilities: PlatformCapability
  data: PlatformOverrideData
  onChange: (data: PlatformOverrideData) => void
  mediaUrl?: string
}

function getCharCountColor(current: number, max: number): string {
  const ratio = current / max
  if (ratio > 1) return 'text-destructive font-semibold'
  if (ratio > 0.9) return 'text-warning'
  return 'text-muted-foreground'
}

export function PlatformTab({
  platform,
  capabilities,
  data,
  onChange,
  mediaUrl,
}: PlatformTabProps) {
  const charCount = data.body.length
  const maxLength = capabilities.max_length
  const isOverLimit = charCount > maxLength
  const platformName = platformLabel(platform)

  return (
    <div className="space-y-3">
      {/* Body textarea with character count */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">{platformName} caption</Label>
          <span
            className={cn('text-[0.625rem] tabular-nums', getCharCountColor(charCount, maxLength))}
          >
            {charCount}/{maxLength}
          </span>
        </div>
        <Textarea
          value={data.body}
          onChange={(e) => onChange({ ...data, body: e.target.value })}
          placeholder={`Write your ${platformName} caption...`}
          rows={4}
          className={cn(
            'resize-none text-xs',
            isOverLimit && 'border-destructive focus-visible:border-destructive'
          )}
          data-testid={`platform-tab-body-${platform}`}
        />
        {isOverLimit && (
          <p className="text-destructive text-[0.625rem]">
            Exceeds {platformName} character limit by {charCount - maxLength} characters
          </p>
        )}
      </div>

      {/* Media preview */}
      {mediaUrl && (
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs font-medium">Media preview</Label>
          <div className="border-border max-w-[200px] overflow-hidden rounded-md border">
            <img src={mediaUrl} alt="Media preview" className="h-auto w-full" />
          </div>
        </div>
      )}

      {/* Platform-specific fields */}
      {capabilities.features.first_comment && (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">First comment (hashtags)</Label>
          <Textarea
            value={data.first_comment}
            onChange={(e) => onChange({ ...data, first_comment: e.target.value })}
            placeholder="#hashtag1 #hashtag2 #hashtag3"
            rows={2}
            className="resize-none text-xs"
            data-testid={`platform-tab-first-comment-${platform}`}
          />
          <p className="text-muted-foreground text-[0.625rem]">
            Hashtags posted as a separate first comment for cleaner captions
          </p>
        </div>
      )}

      {capabilities.features.threads && (
        <div className="border-border flex items-center justify-between rounded-md border px-3 py-2">
          <div>
            <Label className="text-xs font-medium">Thread</Label>
            <p className="text-muted-foreground text-[0.625rem]">
              Split into a thread if over limit
            </p>
          </div>
          <Switch
            checked={data.thread}
            onCheckedChange={(checked) => onChange({ ...data, thread: checked })}
            data-testid={`platform-tab-thread-${platform}`}
          />
        </div>
      )}

      {capabilities.features.article_link && (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Article link</Label>
          <Input
            type="url"
            value={data.article_link}
            onChange={(e) => onChange({ ...data, article_link: e.target.value })}
            placeholder="https://..."
            className="text-xs"
            data-testid={`platform-tab-article-link-${platform}`}
          />
        </div>
      )}
    </div>
  )
}
